import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
  Alert,
  Box,
  Fab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
} from "@mui/material";
import DeleteEntityDialog from "components/DeleteEntityDialog";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import ResponsiveIframe from "components/ResponsiveIframe";
import SnackbarMessage from "components/SnackbarMessage";
import { FORM_ERROR } from "final-form";
import useGlobalContext from "hooks/globalData";
import useBoolean from "hooks/useBoolean";
import useTitle from "hooks/useTitle";
import useTrashablePromises from "hooks/useTrashablePromises";
import { useCallback, useState } from "react";
import { FormattedMessage as T, useIntl } from "react-intl";
import { useQuery, useQueryClient } from "react-query";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import {
  ResponseCode,
  ResponseOfTemplateAdmin,
} from "services/KnowfullyAdminClient";
import contextStore from "stores/contextStore";
import { Severity, SnackbarMessageProps } from "types/common-types";
import translateError from "utils/translateError";

const useStyles = () => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
  paper: {
    border: "4px solid #F0E4D3",
    borderRadius: "5px",
    margin: "20px",
  },
});

const ContentTemplatesPage = () => {
  const styles = useStyles();
  const { formatMessage: t } = useIntl();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    templateService,
    contentTemplateEditUrl,
    contentTemplatesUrl,
    organizationService,
  } = useGlobalContext();
  const [deleteOpen, deleteOpenActions] = useBoolean(false);
  const { registerPromise } = useTrashablePromises();
  const queryClient = useQueryClient();
  const { organizationId, organizationName } = contextStore.useState();

  const [
    snackbarMessage,
    setSnackbarMessage,
  ] = useState<SnackbarMessageProps | null>(null);

  const templateResult = useQuery<ResponseOfTemplateAdmin>(
    ["templatePublished", id],
    () => templateService.getTemplatePublished(id!),
    {
      enabled: !!id,
    }
  );

  const template = templateResult.data?.data;
  useTitle(template?.name || "Template Details");

  const isDescendantOrganization = useQuery(
    [
      "isDescendantOrganization",
      organizationId,
      templateResult.data?.data?.organizationId,
    ],
    () =>
      organizationService.isDescendantOrganization(
        templateResult.data?.data?.organizationId!
      ),
    {
      enabled: !!templateResult.data,
    }
  );

  const isEditingEnabled = isDescendantOrganization?.data?.data;

  const handleDelete = useCallback(async () => {
    try {
      await registerPromise(templateService.deleteTemplate(id!));
      queryClient.invalidateQueries("contentTemplates");
      queryClient.invalidateQueries(["templatePublished", id]);
      queryClient.invalidateQueries(["placeholders", id]);

      navigate(contentTemplatesUrl());
    } catch (error: any) {
      console.log(error);

      if (
        error.status === 200 &&
        error.data?.responseCode === ResponseCode.BusinessRuleFailed
      ) {
        setSnackbarMessage(
          new SnackbarMessageProps(translateError(error, t), Severity.Error)
        );
      }

      return { [FORM_ERROR]: translateError(error, t) };
    }
  }, [
    templateService,
    contentTemplatesUrl,
    navigate,
    id,
    queryClient,
    registerPromise,
    t,
  ]);

  if (!id) return <Navigate to={contentTemplatesUrl()} replace />;
  if (templateResult.isLoading) return <Loader />;
  if (templateResult.isError) {
    return (
      <Box mt={4}>
        <Alert severity="error">
          <T id="templateLoadError" />
        </Alert>
      </Box>
    );
  }

  const rows = [
    {
      title: t({ id: "id" }),
      component: template?.id && <span>{template.id}</span>,
    },
    {
      title: t({ id: "organization" }),
      component: <span>{template?.organizationName}</span>,
    },
    {
      title: t({ id: "nameLabel" }),
      component: template?.name && <span>{template.name}</span>,
    },
    {
      title: t({ id: "typeLabel" }),
      component: template?.templateTypeName && (
        <span>{template.templateTypeName}</span>
      ),
    },
    {
      title: t({ id: "entityLabel" }),
      component: template?.templateEntityName && (
        <span>{template.templateEntityName}</span>
      ),
    },
    {
      title: t({ id: "placeholderNameLabel" }),
      component: template?.placeholderName && (
        <span>{template.placeholderName}</span>
      ),
    },
    {
      title: t({ id: "codeConstantLabel" }),
      component: template?.codeConstant && <span>{template.codeConstant}</span>,
    },
    {
      title: t({ id: "descriptionLabel" }),
      component: template?.description && <span>{template.description}</span>,
    },
    {
      title: t({ id: "contentLabel" }),
      component: template?.content && (
        <Paper sx={styles.paper}>
          <ResponsiveIframe srcDoc={template.content} title="content" />
        </Paper>
      ),
    },
  ].filter((r) => !!r.component);

  return (
    <>
      <RedirectBackButton
        redirectRoute={contentTemplatesUrl()}
        labelKey="backToListButtonLabel"
      />
      <h1>
        <span>{template?.name}</span>
      </h1>

      <Box my={3} display="flex" justifyContent="center">
        <Tooltip
          title={
            !isEditingEnabled &&
            t({ id: "thisTemplateCannotBeEdited" }, { organizationName })
          }
        >
          <span>
            <Fab
              color="primary"
              data-cy="editButton"
              disabled={!isEditingEnabled}
              onClick={() => navigate(contentTemplateEditUrl(id))}
            >
              <EditIcon />
            </Fab>
          </span>
        </Tooltip>
        <Box ml={4} />
        <Fab
          color="secondary"
          onClick={deleteOpenActions.setTrue}
          data-cy="deleteButton"
        >
          <DeleteIcon />
        </Fab>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableBody>
            {rows.map(({ title, component }, i) => (
              <TableRow key={i}>
                <TableCell
                  component="th"
                  scope="row"
                  style={{ fontWeight: "bold" }}
                >
                  {title}
                </TableCell>
                <TableCell>{component}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DeleteEntityDialog
        dialogTitleKey="deleteTemplateTitle"
        contentKey="deleteTemplateInfo"
        onCancel={deleteOpenActions.setFalse}
        handleDelete={handleDelete}
        open={deleteOpen}
      />
      <SnackbarMessage
        snackbarMessage={snackbarMessage}
        setSnackbarMessage={setSnackbarMessage}
      />
    </>
  );
};

export default ContentTemplatesPage;
